export const PATHNAME = "/search";
import * as React from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import { TopMenu } from "app/ui/sections/top-menu";
import { getQueryParam, getQueryParams } from "app/utils/syntax-helpers";
import {
    SearchEngine,
    ChangeListener,
    ChangeEvent,
    ResultData
} from "app/services/data-loader/search-engine.service";
import { SearchResults } from "app/ui/sections/search-results";
import { SearchOptions } from "app/services/data-loader/search-engine.service";
import { NavigateService } from "app/services/navigate.service";

/*
 * Page component for searching a thesaurus.
 */
export const SearchPage: React.FC = () => {
    const [results, setResults] = React.useState<ResultData | undefined>();
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    // Helper to get query params from location
    const getKeyAndOptions = React.useCallback(() => {
        const key = getQueryParam("key", location);
        const options = getQueryParams(["limit", "type", "sort"], location);
        const params = new URLSearchParams(location.search);
        return { key, options, params };
    }, [location]);

    // Listen for search engine changes
    React.useEffect(() => {
        const listener = {
            onChange: (e: ChangeEvent) => {
                const key = e.data.key;
                const options = e.data.options;
                const params = new URLSearchParams(location.search);
                // Build the link and navigate to it
                const link = NavigateService.search(key, options, params);
                navigate(link);
                setResults(e.data);
            }
        };
        SearchEngine.addChangeListener(listener);
        return () => {
            SearchEngine.removeChangeListener(listener);
        };
    }, [navigate, location]);

    // Perform search on mount and when location/params change
    React.useEffect(() => {
        const { key, options } = getKeyAndOptions();
        if (key) {
            searchFor(key, options);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.hash]);

    const searchFor = (key: string, options?: SearchOptions) => {
        options = options || getQueryParams(["limit", "type", "sort"], location);
        if (options && !options.limit) {
            options.limit = SearchEngine.MAXCOUNT_QUICKSEARCH_ITEMS;
        }
        SearchEngine.search(key, options);
    };

    return (
        <>
            <TopMenu quicksearch={false} />
            <Container maxWidth={false}>
                <Grid container>
                    <Grid size={12} className="single-column">
                        <SearchResults results={results} />
                    </Grid>
                </Grid>
            </Container>
        </>
    );
};
